// Cloudflare Turnstile loader — injects the script tag once per page, then
// exposes a render() helper for components. The widget posts an event-style
// callback when the user passes (or fails) the challenge.
//
// Cloudflare publishes always-pass test keys for local dev (see
// .env.example). When VITE_TURNSTILE_SITE_KEY is unset, callers receive an
// empty string token and the backend guard passes through in non-production.

declare global {
  interface Window {
    turnstile?: {
      render: (
        element: HTMLElement | string,
        opts: {
          sitekey: string;
          callback: (token: string) => void;
          'error-callback'?: () => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
        },
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

const SCRIPT_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

let loadPromise: Promise<void> | null = null;

export function loadTurnstileScript(): Promise<void> {
  if (typeof window === 'undefined') return Promise.resolve();
  if (window.turnstile) return Promise.resolve();
  if (loadPromise) return loadPromise;

  loadPromise = new Promise((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src^="${SCRIPT_SRC.split('?')[0]}"]`,
    );
    if (existing) {
      // Script tag already in the DOM but the global hasn't appeared yet —
      // poll briefly.
      let attempts = 0;
      const tick = () => {
        if (window.turnstile) return resolve();
        if (attempts++ > 50) return reject(new Error('turnstile script load timed out'));
        setTimeout(tick, 100);
      };
      tick();
      return;
    }
    const s = document.createElement('script');
    s.src = SCRIPT_SRC;
    s.async = true;
    s.defer = true;
    s.onload = () => resolve();
    s.onerror = () => reject(new Error('failed to load turnstile script'));
    document.head.appendChild(s);
  });
  return loadPromise;
}

export const TURNSTILE_SITE_KEY = (
  import.meta.env.VITE_TURNSTILE_SITE_KEY ?? ''
).trim();
